export const TEAM_ASSISTANT_THEME_STORAGE_KEY = 'market-finder-team-assistant-color-theme';

export const DEFAULT_TEAM_ASSISTANT_THEME_ID = 'classic';

/** @typedef {{ id: string; label: string; description: string; swatches: string[] }} TeamAssistantColorTheme */

/** Generic names only — palette cues from popular social apps, not trademarks. */
/** @type {TeamAssistantColorTheme[]} */
export const TEAM_ASSISTANT_COLOR_THEMES = [
  {
    id: 'classic',
    label: 'Classic',
    description: 'Neutral gray, green send.',
    swatches: ['#f7f7f8', '#f4f4f4', '#10a37f', '#0d0d0d'],
  },
  {
    id: 'harbor-blue',
    label: 'Harbor blue',
    description: 'Bright social blue accent.',
    swatches: ['#f0f2f5', '#e4e6eb', '#1877f2', '#050505'],
  },
  {
    id: 'sunset-studio',
    label: 'Sunset studio',
    description: 'Pink bubble, violet send.',
    swatches: ['#fafafa', '#fce7f3', '#d6249f', '#262626'],
  },
  {
    id: 'ink-timeline',
    label: 'Ink timeline',
    description: 'High-contrast dark accent.',
    swatches: ['#f7f9f9', '#eff3f4', '#0f1419', '#536471'],
  },
  {
    id: 'pulse-neon',
    label: 'Pulse neon',
    description: 'Magenta send, cyan hint.',
    swatches: ['#f8f8f8', '#f1f1f1', '#fe2c55', '#25f4ee'],
  },
  {
    id: 'office-link',
    label: 'Office link',
    description: 'Professional cobalt blue.',
    swatches: ['#f3f6f8', '#e8eef3', '#0a66c2', '#191919'],
  },
  {
    id: 'lemon-pop',
    label: 'Lemon pop',
    description: 'Sunny yellow highlight.',
    swatches: ['#fffef5', '#fff9c4', '#f5c400', '#161616'],
  },
  {
    id: 'forum-orange',
    label: 'Forum orange',
    description: 'Orange send, blue chip.',
    swatches: ['#f8f9fa', '#ffead5', '#ff4500', '#0079d3'],
  },
  {
    id: 'pin-crimson',
    label: 'Pin crimson',
    description: 'Bold red accent.',
    swatches: ['#fafafa', '#fde8e8', '#e60023', '#111111'],
  },
  {
    id: 'thread-gray',
    label: 'Thread gray',
    description: 'Minimal monochrome.',
    swatches: ['#ffffff', '#f5f5f5', '#000000', '#737373'],
  },
  {
    id: 'horizon-sky',
    label: 'Horizon sky',
    description: 'Open sky blue.',
    swatches: ['#f0f8ff', '#dbeafe', '#0085ff', '#0c304a'],
  },
  {
    id: 'fediverse-violet',
    label: 'Fediverse violet',
    description: 'Soft purple accent.',
    swatches: ['#f4f4ff', '#e0e7ff', '#6364ff', '#2f2f47'],
  },
  {
    id: 'midnight-journal',
    label: 'Midnight journal',
    description: 'Navy tint, green send.',
    swatches: ['#eef1f5', '#dce3ec', '#00cf35', '#001935'],
  },
  {
    id: 'baltic-blue',
    label: 'Baltic blue',
    description: 'Cool electric blue.',
    swatches: ['#f0f6ff', '#dce9ff', '#0077ff', '#1a1a1a'],
  },
  {
    id: 'teal-circle',
    label: 'Teal circle',
    description: 'Teal green accent.',
    swatches: ['#f0faf7', '#d1f0e8', '#00af87', '#1c3d36'],
  },
  {
    id: 'neighborhood',
    label: 'Neighborhood',
    description: 'Fresh grass green.',
    swatches: ['#f4fbea', '#e3f5d0', '#8ed500', '#2d5016'],
  },
  {
    id: 'executive-slate',
    label: 'Executive slate',
    description: 'Charcoal gray, refined gold accent.',
    swatches: ['#f4f5f6', '#e8eaed', '#b8860b', '#2c3338'],
  },
  {
    id: 'boardroom-navy',
    label: 'Boardroom navy',
    description: 'Deep navy, crisp silver-blue.',
    swatches: ['#f0f3f8', '#dce4ef', '#1e3a5f', '#0f172a'],
  },
  {
    id: 'trust-emerald',
    label: 'Trust emerald',
    description: 'Institutional green, calm surfaces.',
    swatches: ['#f2f7f4', '#dceee3', '#0f766e', '#134e4a'],
  },
  {
    id: 'capital-burgundy',
    label: 'Capital burgundy',
    description: 'Wine accent on warm ivory.',
    swatches: ['#faf8f6', '#f0e8e4', '#7f1d1d', '#292524'],
  },
  {
    id: 'meridian-steel',
    label: 'Meridian steel',
    description: 'Cool steel gray, cobalt send.',
    swatches: ['#f3f4f6', '#e5e7eb', '#2563eb', '#374151'],
  },
  {
    id: 'prestige-plum',
    label: 'Prestige plum',
    description: 'Muted plum, executive purple.',
    swatches: ['#f8f6f9', '#ede9f2', '#6b21a8', '#3b0764'],
  },
  {
    id: 'summit-stone',
    label: 'Summit stone',
    description: 'Warm stone, bronze highlight.',
    swatches: ['#f7f5f2', '#ebe6df', '#92400e', '#44403c'],
  },
  {
    id: 'compliance-indigo',
    label: 'Compliance indigo',
    description: 'Policy-ready indigo blue.',
    swatches: ['#f5f7ff', '#e0e7ff', '#3730a3', '#1e1b4b'],
  },
  {
    id: 'advisory-sage',
    label: 'Advisory sage',
    description: 'Soft sage, understated green.',
    swatches: ['#f6f7f4', '#e8ece3', '#4d7c0f', '#365314'],
  },
  {
    id: 'pearl-corporate',
    label: 'Pearl corporate',
    description: 'Pearl white, slate blue accent.',
    swatches: ['#fafafa', '#f0f0f0', '#475569', '#0f172a'],
  },
];

export function isValidTeamAssistantThemeId(id) {
  return TEAM_ASSISTANT_COLOR_THEMES.some((t) => t.id === id);
}

export function readStoredTeamAssistantThemeId() {
  if (typeof window === 'undefined') return DEFAULT_TEAM_ASSISTANT_THEME_ID;
  const stored = window.localStorage.getItem(TEAM_ASSISTANT_THEME_STORAGE_KEY);
  return isValidTeamAssistantThemeId(stored) ? stored : DEFAULT_TEAM_ASSISTANT_THEME_ID;
}

export function applyTeamAssistantThemeToDocument(themeId) {
  if (typeof document === 'undefined') return;
  const id = isValidTeamAssistantThemeId(themeId) ? themeId : DEFAULT_TEAM_ASSISTANT_THEME_ID;
  document.documentElement.setAttribute('data-team-assistant-theme', id);
}

export function persistTeamAssistantThemeId(themeId) {
  if (typeof window === 'undefined') return;
  const id = isValidTeamAssistantThemeId(themeId) ? themeId : DEFAULT_TEAM_ASSISTANT_THEME_ID;
  window.localStorage.setItem(TEAM_ASSISTANT_THEME_STORAGE_KEY, id);
  applyTeamAssistantThemeToDocument(id);
}
